import { toast } from "react-toastify";
import { NavigateFunction } from "react-router-dom";

interface ErrorHandlerOptions {
  dispatch: (action: { type: string; payload: any }) => void;

  navigate: NavigateFunction;

  redirectPath?: string;

  customErrorMessage?: string;

  showToast?: boolean;

  logoutOn401?: boolean;

  onHandled?: () => void;
}

export const handleApiError = (
  error: any,
  options: ErrorHandlerOptions
): boolean => {
  const {
    dispatch,
    navigate,
    redirectPath,
    customErrorMessage,
    showToast = true,
    logoutOn401 = true,
    onHandled,
  } = options;

  if (error?.response?.status === 401) {
    console.error("Authentication failed (401) - logging out user");

    if (showToast) {
      toast.error(customErrorMessage || "Session expired. Please login again.");
    }

    if (logoutOn401) {
      dispatch({ type: "LOGOUT", payload: {} });

      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      }
    }

    onHandled?.();
    return true;
  }

  if (error?.response?.data?.code === "RSUSKO026") {
    dispatch({ type: "LOGOUT", payload: {} });

    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }

    onHandled?.();
    return true;
  }

  return false;
};

export const withErrorHandler = <T>(
  fn: () => Promise<T>,
  options: ErrorHandlerOptions,
  fallbackHandler?: (error: any) => void
): (() => Promise<T | undefined>) => {
  return async () => {
    try {
      return await fn();
    } catch (error: any) {
      const wasHandled = handleApiError(error, options);

      if (!wasHandled && fallbackHandler) {
        fallbackHandler(error);
      }

      return undefined;
    }
  };
};

export const createErrorHandler = (options: ErrorHandlerOptions) => {
  return (error: any, overrides?: Partial<ErrorHandlerOptions>): boolean => {
    return handleApiError(error, { ...options, ...overrides });
  };
};
